<!--
  Scenario Builder — ported from the "Quick Payment Calculator" mode of CGS2022
  Scripts/Calculator/CalculatorV2.vue: build up to four purchase or refinance payment
  scenarios and compare them side by side.

  The arithmetic is not in this file. It lives in helpers/paymentMath.js, shared with the
  Payment Calculator, so the two pages cannot quote a borrower different MI on the same
  inputs. Everything here is the form, the capture, and the comparison.

  FROM CGS, KEPT:
  - The four-scenario cap.
  - The default scenario name, `${Loan Type} ${N Years} ${Amortization}`, editable, 34 chars.
  - A scenario is a SNAPSHOT. CGS recomputes nothing after capture (it actually zeroes its
    working state), and neither does this — editing the form afterwards does not silently
    rewrite a scenario you already captured and showed someone.
  - The amortization schedule is only kept for a Fixed loan.

  FROM CGS, DROPPED: the mode-select cards, the cash-out wizard (its own page now), the MI
  rate dialog, Kendo, and the PDF/email/SMS block.
-->
<template>
  <div class="calc-page">
    <PageHeaderPortal>
      <div class="calc-header">
        <div class="calc-header__icon">
          <i class="fa-duotone fa-layer-group"></i>
        </div>
        <div>
          <h1 class="calc-header__title">Scenario Builder</h1>
          <p class="calc-header__sub">Build up to four payment scenarios and compare them side by side</p>
        </div>
      </div>
    </PageHeaderPortal>

    <div class="calc-body">
      <!-- the form ---------------------------------------------------------- -->
      <section class="page-card step-card">
        <div class="step-head">
          <span class="step-icon tone-blue"><i class="fa-light fa-sliders"></i></span>
          <div class="step-head-text">
            <p class="eyebrow">{{ atCapacity ? 'Comparison full' : `Scenario ${scenarios.length + 1} of ${MAX_SCENARIOS}` }}</p>
            <h2>Build a scenario</h2>
            <p class="step-desc">
              Set the terms, then add it to the comparison. Change anything and add another —
              scenarios already captured stay as they were.
            </p>
          </div>
        </div>

        <div class="sb-toggles">
          <div class="sb-toggle">
            <span class="field-label">Loan purpose</span>
            <div class="seg">
              <button v-for="p in PURPOSES" :key="p.key" type="button"
                      class="seg__btn" :class="{ 'is-active': form.loanPurpose === p.key }"
                      @click="form.loanPurpose = p.key">
                <i :class="p.icon"></i> {{ p.label }}
              </button>
            </div>
          </div>
          <div class="sb-toggle">
            <span class="field-label">Loan type</span>
            <div class="seg">
              <button v-for="t in LOAN_TYPES" :key="t.key" type="button"
                      class="seg__btn" :class="{ 'is-active': form.loanType === t.key }"
                      @click="form.loanType = t.key">
                {{ t.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="field-grid">
          <template v-if="isBuy">
            <label class="field">
              <span class="field-label">Purchase price</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="form.purchasePrice" @input="onPrice($event.target.value)"
                       @blur="form.purchasePrice = money2(form.purchasePrice)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Down payment</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="form.downPayment" @input="onDownPayment($event.target.value)"
                       @blur="form.downPayment = money2(form.downPayment)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Down payment %</span>
              <span class="input-shell">
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="form.downPaymentPercentage" @input="onDownPaymentPct($event.target.value)"
                       @blur="form.downPaymentPercentage = rate3(form.downPaymentPercentage)" />
                <span class="affix affix--post">%</span>
              </span>
            </label>
          </template>

          <template v-else>
            <label class="field">
              <span class="field-label">Estimated value</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="form.purchasePrice" @blur="form.purchasePrice = money2(form.purchasePrice)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Amount owed</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="form.amountOwed" @blur="form.amountOwed = money2(form.amountOwed)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Refi type</span>
              <span class="input-shell input-shell--select">
                <select v-model="form.refiType">
                  <option v-for="r in REFI_TYPES" :key="r" :value="r">{{ r }}</option>
                </select>
              </span>
            </label>
            <label v-if="form.refiType === 'Cash Out'" class="field field--new-row">
              <span class="field-label">Cash out</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="form.cashOut" @blur="form.cashOut = money2(form.cashOut)" />
              </span>
            </label>
          </template>

          <label v-if="form.loanType === 'va'" class="field field--new-row">
            <span class="field-label">VA type</span>
            <span class="input-shell input-shell--select">
              <select v-model="form.vaType">
                <option v-for="v in VA_TYPES" :key="v" :value="v">{{ v }}</option>
              </select>
            </span>
          </label>

          <div v-if="form.loanType === 'va' && isBuy" class="field">
            <span class="field-label">First-time use</span>
            <label class="check sb-check">
              <input type="checkbox" v-model="form.firstTime" />
              <span>First time using the VA benefit</span>
            </label>
          </div>

          <label class="field field--new-row">
            <span class="field-label">Amortization type</span>
            <span class="input-shell input-shell--select">
              <select v-model="form.loanAmort">
                <option v-for="a in AMORT_TYPES" :key="a" :value="a">{{ a }}</option>
              </select>
            </span>
          </label>
          <label class="field">
            <span class="field-label">Note rate</span>
            <span class="input-shell">
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.noteRatePct" @blur="form.noteRatePct = rate3(form.noteRatePct)" />
              <span class="affix affix--post">%</span>
            </span>
          </label>
          <label class="field">
            <span class="field-label">Term</span>
            <span class="input-shell">
              <input type="text" inputmode="numeric" autocomplete="off" v-model="form.term" />
              <span class="affix affix--post">months</span>
            </span>
          </label>

          <label class="field field--new-row">
            <span class="field-label">Monthly taxes</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.taxes" @blur="form.taxes = money2(form.taxes)" />
            </span>
          </label>
          <label class="field">
            <span class="field-label">Monthly insurance</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.insurance" @blur="form.insurance = money2(form.insurance)" />
            </span>
          </label>
          <label class="field">
            <span class="field-label">Monthly HOA</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.hoa" @blur="form.hoa = money2(form.hoa)" />
            </span>
          </label>

          <label v-if="form.loanType === 'conv'" class="field field--new-row">
            <span class="field-label">Monthly MI</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.monthlyMI" @blur="form.monthlyMI = money2(form.monthlyMI)" />
            </span>
          </label>
          <label class="field" :class="{ 'field--new-row': form.loanType !== 'conv' }">
            <span class="field-label">Fees for APR</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="form.additionalFees" @blur="form.additionalFees = money2(form.additionalFees)" />
            </span>
          </label>
        </div>

        <div v-if="atCapacity" class="calc-note sb-note">
          <i class="fa-duotone fa-circle-info"></i>
          <div>
            <strong>Four scenarios is the limit</strong>
            Delete one below to make room for another.
          </div>
        </div>

        <div class="step-foot">
          <button type="button" class="secondary-button" @click="resetForm">Reset</button>
          <div class="foot-right">
            <button type="button" class="secondary-button" :disabled="!scenarios.length" @click="clearAll">
              Clear Scenarios
            </button>
            <button type="button" class="primary-button" :disabled="!canAdd" @click="addScenario">
              Add to Comparison
            </button>
          </div>
        </div>
      </section>

      <!-- live preview of the scenario being built ---------------------------- -->
      <aside class="rail--dark">
        <div>
          <p class="eyebrow eyebrow--gold">Working Scenario</p>
          <h3>{{ isBuy ? 'Purchase' : 'Refinance' }} &middot; {{ loanTypeLabel }}</h3>
        </div>

        <div class="rail-hero">
          <span>Total monthly payment</span>
          <strong>{{ live.isPriced ? money(live.totalPayment) : '&mdash;' }}</strong>
        </div>

        <dl class="rail-rows">
          <div><dt>Principal &amp; interest</dt><dd>{{ live.isPriced ? money(live.pandi) : '&mdash;' }}</dd></div>
          <div><dt>Mortgage insurance</dt><dd>{{ money(live.pmi) }}</dd></div>
          <div><dt>Taxes</dt><dd>{{ money(live.taxes) }}</dd></div>
          <div><dt>Insurance</dt><dd>{{ money(live.insurance) }}</dd></div>
          <div><dt>HOA</dt><dd>{{ money(live.hoa) }}</dd></div>
        </dl>

        <dl class="rail-rows rail-rows--divided">
          <div v-if="live.upFrontFee > 0"><dt>{{ live.upFrontFeeLabel }}</dt><dd>{{ money(live.upFrontFee) }}</dd></div>
          <div><dt>Loan amount</dt><dd>{{ money(live.loanAmount) }}</dd></div>
          <div><dt>LTV</dt><dd>{{ pct(live.ltv) }}</dd></div>
          <div><dt>APR</dt><dd>{{ pct(live.apr) }}</dd></div>
        </dl>

        <div class="rail-highlight">
          <span>Scenarios captured</span>
          <strong>{{ scenarios.length }} of {{ MAX_SCENARIOS }}</strong>
        </div>
      </aside>
    </div>

    <!-- the comparison -------------------------------------------------------- -->
    <section class="page-card">
      <div class="step-head">
        <span class="step-icon tone-green"><i class="fa-light fa-table-columns"></i></span>
        <div class="step-head-text">
          <p class="eyebrow">Side-by-side comparison</p>
          <h2>{{ scenarios.length ? `Comparing ${scenarios.length} scenario${scenarios.length === 1 ? '' : 's'}` : 'No scenarios yet' }}</h2>
          <p class="step-desc">
            {{ scenarios.length
                ? 'The lowest total payment is highlighted. Each column is a snapshot taken when you added it.'
                : 'Set some terms above and choose Add to Comparison. You can hold up to four at once.' }}
          </p>
        </div>
      </div>

      <div v-if="scenarios.length" class="sb-grid" :style="{ '--cols': scenarios.length }">
        <div v-for="(s, i) in scenarios" :key="s.id" class="sb-col" :class="{ 'is-best': s.id === bestId }">
          <div class="sb-col-head">
            <span class="input-shell sb-name">
              <input type="text" class="sb-name-input" :maxlength="NAME_MAX"
                     :value="s.name" @input="s.name = $event.target.value" />
            </span>
            <small>{{ (s.name || '').length }}/{{ NAME_MAX }}</small>
          </div>

          <p v-if="s.id === bestId && scenarios.length > 1" class="sb-flag">Lowest payment</p>

          <div class="stat-box">
            <span>Total monthly payment</span>
            <strong>{{ money(s.calc.totalPayment) }}</strong>
          </div>

          <dl class="compare-rows">
            <div><dt>Loan purpose</dt><dd>{{ s.input.loanPurpose === 'Buy' ? 'Purchase' : s.input.refiType }}</dd></div>
            <div><dt>Loan type</dt><dd>{{ labelFor(s.input.loanType) }}</dd></div>
            <div v-if="s.input.loanType === 'va'"><dt>VA type</dt><dd>{{ s.input.vaType }}</dd></div>
            <div v-if="s.input.loanType === 'va' && s.input.loanPurpose === 'Buy'">
              <dt>First-time use</dt><dd>{{ s.input.firstTime ? 'Yes' : 'No' }}</dd>
            </div>
            <div><dt>Amortization</dt><dd>{{ s.input.loanAmort }}</dd></div>
            <div><dt>Loan amount</dt><dd>{{ money(s.calc.loanAmount) }}</dd></div>
            <div><dt>LTV</dt><dd>{{ pct(s.calc.ltv) }}</dd></div>
            <div><dt>Note rate</dt><dd>{{ pct(s.calc.annualPct) }}</dd></div>
            <div><dt>Term</dt><dd>{{ s.calc.termMonths }} months</dd></div>
            <div><dt>APR</dt><dd>{{ pct(s.calc.apr) }}</dd></div>
            <div><dt>Principal &amp; interest</dt><dd>{{ money(s.calc.pandi) }}</dd></div>
            <div v-if="s.calc.pmi > 0"><dt>Mortgage insurance</dt><dd>{{ money(s.calc.pmi) }}</dd></div>
            <div v-if="s.calc.taxes > 0"><dt>Taxes</dt><dd>{{ money(s.calc.taxes) }}</dd></div>
            <div v-if="s.calc.insurance > 0"><dt>Insurance</dt><dd>{{ money(s.calc.insurance) }}</dd></div>
            <div v-if="s.calc.hoa > 0"><dt>HOA</dt><dd>{{ money(s.calc.hoa) }}</dd></div>
            <div v-if="s.calc.upFrontFee > 0">
              <dt>{{ s.calc.upFrontFeeLabel }}</dt><dd>{{ money(s.calc.upFrontFee) }}</dd>
            </div>
            <div><dt>Total interest paid</dt><dd>{{ s.calc.amortization.length ? money(s.calc.totalInterestPaid) : '—' }}</dd></div>
            <div v-if="scenarios.length > 1">
              <dt>vs lowest</dt>
              <dd :class="s.id === bestId ? 'is-good' : 'is-bad'">
                {{ s.id === bestId ? 'lowest' : money(s.calc.totalPayment - bestTotal) + ' more' }}
              </dd>
            </div>
          </dl>

          <div class="sb-col-foot">
            <button type="button" class="link-button" :disabled="!s.calc.amortization.length"
                    @click="toggleAmort(s.id)">
              {{ openAmortId === s.id ? 'Hide' : 'Show' }} amortization
            </button>
            <button type="button" class="link-button sb-delete" @click="removeScenario(i)">Delete</button>
          </div>
        </div>
      </div>

      <div v-if="scenarios.length" class="step-foot">
        <button type="button" class="secondary-button" @click="copySummary">
          {{ copied ? 'Copied' : 'Copy Comparison' }}
        </button>
        <div class="foot-right">
          <button type="button" class="primary-button" :disabled="pdfBusy" @click="generatePdf">
            {{ pdfBusy ? 'Generating...' : 'Generate PDF' }}
          </button>
        </div>
      </div>
    </section>

    <!-- amortization for one scenario ----------------------------------------- -->
    <section v-if="openAmort" class="page-card">
      <div class="step-head">
        <span class="step-icon tone-slate"><i class="fa-light fa-table-list"></i></span>
        <div class="step-head-text">
          <p class="eyebrow">Schedule</p>
          <h2>{{ openAmort.name }}</h2>
          <p class="step-desc">
            {{ openAmort.calc.amortization.length }} payments, {{ money(openAmort.calc.totalInterestPaid) }}
            of interest over the life of the loan.
          </p>
        </div>
      </div>
      <div class="sb-amort-scroll">
        <table class="calc-table">
          <thead>
            <tr><th>Payment</th><th>Interest paid</th><th>Principal paid</th><th>Balance remaining</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in openAmort.calc.amortization" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ money(row.interest) }}</td>
              <td>{{ money(row.principal) }}</td>
              <td>{{ money(row.balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <CalculatorPdfDialog :open="pdfOpen"
                         :busy="pdfBusy"
                         :error="pdfError"
                         :blob-url="pdfBlobUrl"
                         :hosted-url="pdfHostedUrl"
                         :title="pdfTitle()"
                         :file-name="pdfFileName()"
                         @close="closePdf" />
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import CalculatorPdfDialog from "@/components/CalculatorPdfDialog.vue"
  import auth from "@/authConfig.js"
  import { buildPdfPayload, generatePdf } from "@/helpers/calculatorPdf.js"
  import {
    LOAN_TYPES, REFI_TYPES, VA_TYPES, AMORT_TYPES,
    num, hasValue, priceScenario, termLabel, money, pct
  } from "@/helpers/paymentMath.js"

  const MAX_SCENARIOS = 4
  const NAME_MAX = 34

  const PURPOSES = [
    { key: "Buy", label: "Purchase", icon: "fa-light fa-house" },
    { key: "Refi", label: "Refinance", icon: "fa-light fa-arrows-rotate" }
  ]

  function blankForm () {
    return {
      loanPurpose: "Buy",
      loanType: "conv",
      refiType: "No Cash Out",
      vaType: "Regular Military/Active Duty",
      loanAmort: "Fixed",
      firstTime: true,
      purchasePrice: "",
      downPayment: "",
      downPaymentPercentage: "",
      amountOwed: "",
      cashOut: "",
      noteRatePct: "",
      term: "360",
      taxes: "",
      insurance: "",
      hoa: "",
      monthlyMI: "",
      additionalFees: ""
    }
  }

  export default {
    name: "ScenarioBuilder",
    components: { PageHeaderPortal, CalculatorPdfDialog },

    data () {
      return {
        MAX_SCENARIOS,
        NAME_MAX,
        PURPOSES,
        LOAN_TYPES,
        REFI_TYPES,
        VA_TYPES,
        AMORT_TYPES,
        form: blankForm(),
        scenarios: [],
        nextId: 1,
        openAmortId: null,
        copied: false,
        pdfOpen: false,
        pdfBusy: false,
        pdfError: "",
        pdfBlobUrl: "",
        pdfHostedUrl: ""
      }
    },

    computed: {
      isBuy () { return this.form.loanPurpose === "Buy" },
      loanTypeLabel () { return this.labelFor(this.form.loanType) },

      // The live preview. Recomputes on every keystroke; captured scenarios do not.
      live () { return priceScenario(this.form) },

      canAdd () {
        return !this.atCapacity && this.live.isPriced && this.live.loanAmount > 0
      },
      atCapacity () { return this.scenarios.length >= MAX_SCENARIOS },

      bestTotal () {
        if (!this.scenarios.length) return 0
        return Math.min(...this.scenarios.map(s => s.calc.totalPayment))
      },
      bestId () {
        const best = this.scenarios.find(s => s.calc.totalPayment === this.bestTotal)
        return best ? best.id : null
      },
      openAmort () {
        return this.scenarios.find(s => s.id === this.openAmortId) || null
      }
    },

    methods: {
      num,
      money,
      pct,
      labelFor (key) { return (LOAN_TYPES.find(t => t.key === key) || {}).label || "" },

      money2 (raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      rate3 (raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toFixed(3)
      },

      // Price, down payment and down payment % are one relationship with three faces; whichever
      // the user touches drives the others.
      onPrice (raw) {
        this.form.purchasePrice = raw
        const price = num(raw)
        if (price > 0 && hasValue(this.form.downPaymentPercentage)) {
          this.form.downPayment = this.money2(String((price * num(this.form.downPaymentPercentage)) / 100))
        } else if (price > 0 && hasValue(this.form.downPayment)) {
          this.form.downPaymentPercentage = this.rate3(String((num(this.form.downPayment) / price) * 100))
        }
      },
      onDownPayment (raw) {
        this.form.downPayment = raw
        const price = num(this.form.purchasePrice)
        if (price > 0) {
          this.form.downPaymentPercentage = hasValue(raw) ? this.rate3(String((num(raw) / price) * 100)) : ""
        }
      },
      onDownPaymentPct (raw) {
        this.form.downPaymentPercentage = raw
        const price = num(this.form.purchasePrice)
        if (price > 0) {
          this.form.downPayment = hasValue(raw) ? this.money2(String((price * num(raw)) / 100)) : ""
        }
      },

      defaultName () {
        // CGS: `${type} ${mortgageTerm} ${loanAmort}` — e.g. "Conventional 30 Years Fixed".
        const name = `${this.labelFor(this.form.loanType)} ${termLabel(this.form.term)} ${this.form.loanAmort}`
        return name.slice(0, NAME_MAX)
      },

      addScenario () {
        if (!this.canAdd) return
        // Deep-copied inputs and a frozen result: a scenario is what it was when captured, so
        // editing the form afterwards cannot rewrite a column you have already shown someone.
        this.scenarios.push({
          id: this.nextId++,
          name: this.defaultName(),
          input: { ...this.form },
          calc: this.live
        })
      },
      removeScenario (i) {
        const [gone] = this.scenarios.splice(i, 1)
        if (gone && gone.id === this.openAmortId) this.openAmortId = null
      },
      clearAll () {
        this.scenarios = []
        this.openAmortId = null
      },
      toggleAmort (id) {
        this.openAmortId = this.openAmortId === id ? null : id
      },
      resetForm () {
        this.form = blankForm()
      },


      // ── PDF ──────────────────────────────────────────────────────────────
      // Posts to the same CGS endpoint the Scenario Builder page in CGS uses; the payload
      // shape is a server-side contract (see helpers/calculatorPdf.js).
      async generatePdf () {
        if (this.pdfBusy) return
        this.revokePdf()
        this.pdfOpen = true
        this.pdfBusy = true
        this.pdfError = ""
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const { blobUrl, hostedUrl } = await generatePdf({
            payload: buildPdfPayload({
              // Every captured scenario becomes a column. The "Best monthly" badge is not
              // set here: the report only badges a card inside the Cash-Out Payoff Comparison,
              // and these are ordinary payment scenarios, so CGS leaves them all unflagged.
              scenarios: this.scenarios.map(s => ({
                name: s.name,
                input: s.input,
                calc: s.calc
              })),
              context: { homeValue: num(this.scenarios[0]?.input?.purchasePrice) }
            }),
            token: tokenResponse?.accessToken
          })
          this.pdfBlobUrl = blobUrl
          this.pdfHostedUrl = hostedUrl
        } catch (e) {
          this.pdfError = e?.response?.data?.message || e?.message || "The PDF service did not respond."
        } finally {
          this.pdfBusy = false
        }
      },
      closePdf () {
        this.pdfOpen = false
        this.revokePdf()
      },
      // A blob URL holds the whole document in memory until it is revoked.
      revokePdf () {
        if (this.pdfBlobUrl) {
          try { URL.revokeObjectURL(this.pdfBlobUrl) } catch (e) { /* already gone */ }
        }
        this.pdfBlobUrl = ""
        this.pdfHostedUrl = ""
        this.pdfError = ""
      },
      pdfTitle () { return `${this.scenarios.length} payment scenario${this.scenarios.length === 1 ? "" : "s"}` },
      pdfFileName () { return "Payment_Scenarios.pdf" },

      async copySummary () {
        const l = ["Payment scenarios", ""]
        for (const s of this.scenarios) {
          l.push(`${s.name}${s.id === this.bestId && this.scenarios.length > 1 ? "  <= lowest" : ""}`)
          l.push(`  ${s.input.loanPurpose === "Buy" ? "Purchase" : s.input.refiType} / ${this.labelFor(s.input.loanType)} / ${s.input.loanAmort}`)
          l.push(`  Loan ${money(s.calc.loanAmount)} at ${pct(s.calc.ltv)} LTV`)
          l.push(`  ${pct(s.calc.annualPct)} for ${s.calc.termMonths} months, APR ${pct(s.calc.apr)}`)
          l.push(`  P&I ${money(s.calc.pandi)}, MI ${money(s.calc.pmi)}, taxes ${money(s.calc.taxes)}, ins ${money(s.calc.insurance)}, HOA ${money(s.calc.hoa)}`)
          if (s.calc.upFrontFee > 0) l.push(`  ${s.calc.upFrontFeeLabel}: ${money(s.calc.upFrontFee)}`)
          l.push(`  Total monthly: ${money(s.calc.totalPayment)}`)
          if (s.calc.amortization.length) l.push(`  Total interest paid: ${money(s.calc.totalInterestPaid)}`)
          l.push("")
        }
        try {
          await navigator.clipboard.writeText(l.join("\n"))
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          this.copied = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Shared look comes from assets/css/calculatorKit.css; only this page's layout is here. */
  .sb-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 22px;
    margin-top: 22px;
  }

  .sb-toggle {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }

  .sb-check { height: var(--control-h); }

  .sb-note { margin-top: 20px; }

  /* One column per captured scenario, never wider than a quarter of the row. --cols is set
     inline so two scenarios read as two halves rather than two quarters and a gap. */
  .sb-grid {
    display: grid;
    grid-template-columns: repeat(var(--cols, 1), minmax(0, 1fr));
    gap: 16px;
    margin-top: 22px;
  }

  .sb-col {
    border: 1px solid var(--calc-line);
    border-radius: 10px;
    padding: 16px;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .sb-col.is-best {
    background: var(--calc-gold-bg);
    border-color: var(--calc-gold-edge);
  }

  .sb-col-head {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sb-name { height: calc(var(--control-h) - 4px); }

  .sb-name-input {
    text-align: left !important;
    font-weight: 700 !important;
  }

  .sb-col-head small {
    align-self: flex-end;
    font-size: var(--fs-2xs);
    color: var(--calc-dim);
  }

  .sb-flag {
    margin: 8px 0 0;
    font-size: var(--fs-2xs);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--calc-gold);
  }

  .sb-col-foot {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: auto;
    padding-top: 14px;
  }

  .sb-delete { color: #a4472f; }

  .sb-amort-scroll {
    max-height: 420px;
    overflow: auto;
    margin-top: 18px;
    border: 1px solid var(--calc-line);
    border-radius: 9px;
  }

  /* Four columns of financial rows stop being readable well before the page gets narrow. */
  @media (max-width: 1500px) {
    .sb-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @media (max-width: 900px) {
    .sb-grid { grid-template-columns: minmax(0, 1fr); }
  }
</style>
